const COLORS = {
  'electric-blue': '#35b8ff',
  'hot-pink': '#ff4fc8',
  'acid-lime': '#b9ff4f',
  'solar-gold': '#ffd24f',
  'lava-red': '#ff5a5f',
  ultraviolet: '#8a6dff',
};

export class PartyVisuals {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.clue = null;
    this.burstUntil = 0;
    this.particles = Array.from({ length: 54 }, (_, index) => ({
      x: ((index * 47) % 101) / 100,
      y: ((index * 71) % 103) / 102,
      size: 0.7 + (index % 5) * 0.55,
      speed: 0.00006 + (index % 7) * 0.000015,
      drift: (index % 2 ? 1 : -1) * (0.00002 + (index % 3) * 0.00001),
    }));
    this.resize = this.resize.bind(this);
    window.addEventListener('resize', this.resize);
    this.resize();
    requestAnimationFrame((time) => this.draw(time));
  }

  resize() {
    const ratio = Math.min(2, window.devicePixelRatio || 1);
    this.canvas.width = Math.floor(innerWidth * ratio);
    this.canvas.height = Math.floor(innerHeight * ratio);
    this.canvas.style.width = `${innerWidth}px`;
    this.canvas.style.height = `${innerHeight}px`;
    this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  }

  setSignal(clue) {
    this.clue = clue || null;
  }

  celebrate() {
    this.burstUntil = performance.now() + 1800;
  }

  draw(time) {
    const { ctx } = this;
    const width = innerWidth;
    const height = innerHeight;
    ctx.clearRect(0, 0, width, height);
    const accent = COLORS[this.clue?.color] || '#8a6dff';

    for (const particle of this.particles) {
      particle.y -= particle.speed * 16;
      particle.x += particle.drift * Math.sin(time * 0.0004) * 10;
      if (particle.y < -0.03) particle.y = 1.03;
      if (particle.x < -0.03) particle.x = 1.03;
      if (particle.x > 1.03) particle.x = -0.03;
      const pulse = 0.45 + Math.sin(time * 0.0015 + particle.x * 12) * 0.2;
      ctx.fillStyle = hexAlpha(accent, this.clue ? 0.08 + pulse * 0.08 : 0.05);
      ctx.beginPath();
      ctx.arc(particle.x * width, particle.y * height, particle.size * (this.clue ? 1.5 : 1), 0, Math.PI * 2);
      ctx.fill();
    }

    if (this.clue) this.drawSignal(time, accent, width, height);
    if (time < this.burstUntil) this.drawBurst(time, width, height);
    requestAnimationFrame((next) => this.draw(next));
  }

  drawSignal(time, color, width, height) {
    const centerX = width / 2;
    const centerY = height * 0.42;
    const intensity = 0.55 + Number(this.clue.intensity || 50) / 180;
    const t = time / 1000;
    ctxGlow(this.ctx, color, 28);
    this.ctx.strokeStyle = hexAlpha(color, 0.12);
    this.ctx.lineWidth = 2;
    const rings = 3;
    for (let index = 0; index < rings; index += 1) {
      let radius = 90 + index * 55;
      let x = centerX;
      let y = centerY;
      if (this.clue.motion === 'bounce') y += Math.abs(Math.sin(t * 3 + index)) * 42 - 22;
      if (this.clue.motion === 'spiral') {
        x += Math.cos(t * 2 + index * 2) * 35;
        y += Math.sin(t * 2 + index * 2) * 35;
      }
      if (this.clue.motion === 'burst') radius *= 0.75 + ((t * 1.4 + index / rings) % 1) * 0.8;
      if (this.clue.motion === 'drift') x += Math.sin(t + index) * 80;
      if (this.clue.motion === 'shake') x += Math.sin(t * 24 + index) * 10;
      if (this.clue.motion === 'orbit') {
        x += Math.cos(t * 2 + index * 2.1) * 70;
        y += Math.sin(t * 2 + index * 2.1) * 45;
      }
      this.ctx.globalAlpha = intensity * (0.28 - index * 0.055);
      this.ctx.beginPath();
      this.ctx.arc(x, y, radius, 0, Math.PI * 2);
      this.ctx.stroke();
    }
    this.ctx.globalAlpha = 1;
    this.ctx.shadowBlur = 0;
  }

  drawBurst(time, width, height) {
    const age = 1 - (this.burstUntil - time) / 1800;
    const alpha = Math.max(0, 1 - age);
    for (let index = 0; index < 60; index += 1) {
      const angle = index * 2.399;
      const distance = age * (80 + (index % 9) * 34);
      const x = width / 2 + Math.cos(angle) * distance;
      const y = height * 0.38 + Math.sin(angle) * distance + age * age * 160;
      this.ctx.fillStyle = hexAlpha(Object.values(COLORS)[index % 6], alpha * 0.9);
      this.ctx.fillRect(x, y, 4 + (index % 4), 8 + (index % 5));
    }
  }
}

function ctxGlow(ctx, color, amount) {
  ctx.shadowColor = color;
  ctx.shadowBlur = amount;
}

function hexAlpha(hex, alpha) {
  const clean = hex.replace('#', '');
  const bigint = Number.parseInt(clean, 16);
  const red = (bigint >> 16) & 255;
  const green = (bigint >> 8) & 255;
  const blue = bigint & 255;
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export const signalColors = COLORS;
